import type { HelpVideo } from './videos';

/**
 * Entscheidungslogik + Persistenz-Parsing für die Video-Hinweise.
 *
 * Bewusst frei von React, damit die Sichtbarkeitsregel ohne DOM testbar ist
 * (analog `content-visibility.spec.ts`).
 */

/** Globaler Schalter „Video-Hinweise anzeigen" (Einstellungen). */
export const VIDEO_HINTS_ENABLED_KEY = 'ignisai.video-hints';

/** Pro Video weggeklickte Hinweise (Bestandsschlüssel, nicht umbenennen). */
export const VIDEO_HINTS_DISMISSED_KEY = 'ignisai-video-hint-dismissed';

/** Default: an — neue Nutzer sollen die Hinweise sehen und selbst abschalten. */
export const VIDEO_HINTS_DEFAULT_ENABLED = true;

/** Liest die gespeicherte Einstellung; ohne Eintrag gilt der Default. */
export function parseVideoHintsEnabled(raw: string | null): boolean {
  if (raw === null) return VIDEO_HINTS_DEFAULT_ENABLED;
  return raw === 'true';
}

/** Liest die Liste weggeklickter Video-Slugs; defekte Daten ergeben eine leere Menge. */
export function parseDismissedVideoHints(raw: string | null): Set<string> {
  if (!raw) return new Set();
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((slug): slug is string => typeof slug === 'string'));
  } catch {
    return new Set();
  }
}

interface VideoHintVisibilityInput {
  /** Globaler Schalter aus den Einstellungen. */
  enabled: boolean;
  /** Zur Route passendes Video — `null`, wenn es keines gibt. */
  video: HelpVideo | null;
  /** Bereits weggeklickte Video-Slugs. */
  dismissed: ReadonlySet<string>;
}

/**
 * Ein Video-Hinweis erscheint nur, wenn der globale Schalter an ist, ein Video
 * zur Seite existiert und dieses Video nicht einzeln weggeklickt wurde.
 */
export function shouldShowVideoHint({
  enabled,
  video,
  dismissed,
}: VideoHintVisibilityInput): boolean {
  if (!enabled || !video) return false;
  return !dismissed.has(video.slug);
}
